import React from 'react';
import PropTypes from 'prop-types';
import striptags from 'striptags';
import { useGlobalContext } from 'globalContext';
import { Helmet } from 'react-helmet';
import { TitleWrapper, ButtonFullscreen } from 'msp-components';
import ButtonShare from 'components/ButtonShare';
import MediaTitle from './MediaTitle';
import ImageTitle from './ImageTitle';
import SlideshowTitle from './SlideshowTitle';
import PinMapTitle from './PinMapTitle';
import PlaylistTitle from './PlaylistTitle';
import TimelineTitle from './TimelineTitle';
import CoverTitle from './CoverTitle';

const Title = ({ routeProps, menuItems, project, data }) => {
  const { isInternalEmbed } = useGlobalContext();

  const titleComponents = {
    video: MediaTitle,
    audio: MediaTitle,
    image: ImageTitle,
    slideshow: SlideshowTitle,
    'pin map': PinMapTitle,
    playlist: PlaylistTitle,
    timeline: TimelineTitle,
    cover: CoverTitle,
    'topic tree': undefined,
    'page group': undefined,
  };

  const type = data.type.toLowerCase();

  const fsEnabledTypes = ['playlist'];
  const allowFullscreen = fsEnabledTypes.includes(type);

  const Component = titleComponents[type];
  const fallback = process.env.NODE_ENV === 'development' ? (
    <div style={{ height: '100%', overflow: 'auto' }}>
      <pre>{JSON.stringify(data, null, 2)}</pre>
    </div>
  ) : null;
  if (!Component) return fallback;

  const childProps = { routeProps, menuItems, ...data };
  const { displayOptions } = childProps;
  const { itemName, itemShortDescription } = displayOptions.menuItems;
  const menuProps = {
    title: itemName ? data.name : null,
    description: itemShortDescription ? data.shortDescription : null,
    items: menuItems,
    hidePoweredBy: !isInternalEmbed,
  };

  const { hideControlBar, menuDefaultState } = displayOptions;
  const afterControls = type === 'video' ? null : (
    <>
      <ButtonShare />
      <ButtonFullscreen />
    </>
  );

  return (
    <>
      <Helmet>
        <title>{data.name} | {project.name}</title>
        <meta name="description" content={striptags(data.shortDescription || project.shortDescription)} />
      </Helmet>
      {hideControlBar ? (
        <Component key={routeProps.match.url} {...childProps} />
      ) : (
        <TitleWrapper
          allowFullscreen={allowFullscreen}
          menuDefaultState={menuDefaultState}
          menuProps={menuProps}
          afterControls={afterControls}
        >
          <Component key={routeProps.match.url} {...childProps} />
        </TitleWrapper>
      )}
    </>
  );
};

Title.propTypes = {
  routeProps: PropTypes.object,
  menuItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    content: PropTypes.string,
    isVisible: PropTypes.bool,
  })),
  project: PropTypes.shape({
    uid: PropTypes.string,
    name: PropTypes.string,
    slug: PropTypes.string,
    type: PropTypes.string,
    shortDescription: PropTypes.string,
    longDescription: PropTypes.string,
    socialImage: PropTypes.string,
    analyticsProvider: PropTypes.string,
    analyticsId: PropTypes.string,
    shareUrl: PropTypes.string,
    publisher: PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
      twitter: PropTypes.string,
      facebook: PropTypes.string,
    }),
  }),
  data: PropTypes.object,
};

export default Title;
